import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, InputAdornment, Alert, Grid, Chip, Tooltip, IconButton
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import GroupIcon from "@mui/icons-material/Group";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { salvaLog } from "../utils/logger";
import { doc, getDoc, collection, onSnapshot, query, where, addDoc, serverTimestamp, deleteDoc, updateDoc } from "firebase/firestore";
import { parseReportPresenze, minutiInStringa, secondiInHHMMSS, calcolaDurataEventoSecondi, calcolaDurataProgrammataSecondi, nomeCognomePartecipante } from "../utils/parseReportPresenze";
import { leggiIscritti, abbinaIscritti } from "../utils/iscritti";
import * as XLSX from "sheetjs-style";

export default function ReportPresenze() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [evento, setEvento] = useState(null);
  const [clientiMap, setClientiMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [caricando, setCaricando] = useState(false);
  const [ricerca, setRicerca] = useState("");
  const [partecipanti, setPartecipanti] = useState([]);
  const [stats, setStats] = useState(null);
  const [errore, setErrore] = useState(null);
  const [durataEventoSecondi, setDurataEventoSecondi] = useState(null);
  const [abbinando, setAbbinando] = useState(false);
  const [esitoAbbinamento, setEsitoAbbinamento] = useState(null);
  const [durataEffettiva, setDurataEffettiva] = useState("");
  const [salvandoDurata, setSalvandoDurata] = useState(false);

  useEffect(() => {
    getDoc(doc(db, "eventi", id)).then((snap) => {
      if (snap.exists()) {
        const ev = { id: snap.id, ...snap.data() };
        setEvento(ev);
        setDurataEventoSecondi(calcolaDurataEventoSecondi(ev));
        setDurataEffettiva(ev.durataEffettivaMinuti > 0 ? String(ev.durataEffettivaMinuti) : "");
      }
      setLoading(false);
    });

    onSnapshot(collection(db, "clienti"), (snap) => {
      const map = {};
      snap.docs.forEach((d) => { map[d.id] = d.data().nome; });
      setClientiMap(map);
    });
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const q = query(collection(db, "reportPresenze"), where("eventoId", "==", id));
    const unsub = onSnapshot(q, (snap) => {
      const p = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      p.sort((a, b) => (b.durataSecondi || b.durataMinuti * 60) - (a.durataSecondi || a.durataMinuti * 60));
      setPartecipanti(p);
      if (p.length > 0) {
        const durataMedia = Math.round(
          p.reduce((acc, x) => acc + x.durataMinuti, 0) / p.length
        );
        setStats({ totale: p.length, durataMedia });
      } else {
        setStats(null);
      }
    });
    return unsub;
  }, [id]);

  async function handleCaricaFile(e) {
    const file = e.target.files[0];
    // Senza azzerarlo, riselezionare lo stesso file (anche se modificato) non scatena onChange
    e.target.value = "";
    if (!file) return;
    setCaricando(true);
    setErrore(null);
    try {
      const risultato = await parseReportPresenze(file);

      // Un CSV risalvato da Excel o da un editor perde il formato originale (UTF-16 con tab):
      // il parsing non trova nessuno e non deve cancellare il report già caricato.
      if (!risultato.partecipanti.length) {
        setErrore(
          "Nessun partecipante trovato nel file. Carica il CSV originale esportato da Teams, " +
          "senza modificarlo: per togliere un nominativo usa il cestino nell'elenco partecipanti."
        );
        setCaricando(false);
        return;
      }

      const esistenti = await new Promise((resolve) => {
        const q = query(collection(db, "reportPresenze"), where("eventoId", "==", id));
        onSnapshot(q, (snap) => resolve(snap.docs));
      });
      await Promise.all(esistenti.map((d) => deleteDoc(d.ref)));

      await Promise.all(risultato.partecipanti.map((p) =>
        addDoc(collection(db, "reportPresenze"), {
          eventoId: id,
          nome: p.nome,
          email: p.email,
          durataMinuti: p.durataMinuti,
          durataSecondi: p.durataSecondi || p.durataMinuti * 60,
          createdAt: serverTimestamp(),
        })
      ));

      setStats({
        totale: risultato.totalePartecipanti,
        durataMedia: risultato.durataMedia,
      });
    } catch (err) {
      console.error("Errore parsing:", err);
      setErrore("Errore nel parsing del file. Verifica che sia un report Teams valido.");
    }
    setCaricando(false);
  }

  async function handleCaricaIscritti(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setAbbinando(true);
    setErrore(null);
    setEsitoAbbinamento(null);
    try {
      const iscritti = await leggiIscritti(file);
      if (!iscritti.length) throw new Error("Il file iscritti non contiene righe.");
      const abbinamenti = abbinaIscritti(partecipanti, iscritti);

      await Promise.all(partecipanti.map((p, i) => {
        const a = abbinamenti[i];
        const dati = a
          ? { abbinatoIscritti: true, nomeIscritto: a.nomeIscritto, cognomeIscritto: a.cognomeIscritto }
          : { abbinatoIscritti: false, nomeIscritto: null, cognomeIscritto: null };
        // Email arricchita dagli iscritti solo se Teams non la riporta
        if (a && !p.email && a.emailIscritto) dati.email = a.emailIscritto;
        return updateDoc(doc(db, "reportPresenze", p.id), dati);
      }));

      const trovati = abbinamenti.filter(Boolean).length;
      setEsitoAbbinamento({ file: file.name, trovati, nonTrovati: partecipanti.length - trovati });
    } catch (err) {
      console.error("Errore abbinamento iscritti:", err);
      setErrore(err.message || "Errore nella lettura del file iscritti.");
    }
    setAbbinando(false);
  }

  // Rimuove il partecipante dal report (anche lato cliente). Ricaricando il CSV Teams ricompare.
  async function eliminaPartecipante(p) {
    const etichetta = p.email ? `${p.nome} (${p.email})` : p.nome;
    if (!window.confirm(`Vuoi eliminare "${etichetta}" dal report presenze? Ricompare se ricarichi il CSV Teams.`)) return;
    setErrore(null);
    try {
      await deleteDoc(doc(db, "reportPresenze", p.id));
      salvaLog(currentUser, "Eliminazione partecipante report presenze", id, evento?.titolo, [
        { campo: "Partecipante", da: etichetta, a: "—" }
      ]);
    } catch (err) {
      console.error("Errore eliminazione partecipante:", err);
      setErrore("Errore nell'eliminazione del partecipante.");
    }
  }

  // Durata effettiva (minuti) salvata sull'evento: se vuota torna a valere quella programmata
  async function salvaDurataEffettiva(valore) {
    const minuti = valore === "" ? null : Math.round(Number(valore));
    if (minuti !== null && !(minuti > 0)) {
      setErrore("La durata effettiva deve essere un numero di minuti maggiore di zero.");
      return;
    }
    setSalvandoDurata(true);
    setErrore(null);
    try {
      await updateDoc(doc(db, "eventi", id), { durataEffettivaMinuti: minuti });
      const ev = { ...evento, durataEffettivaMinuti: minuti };
      setEvento(ev);
      setDurataEventoSecondi(calcolaDurataEventoSecondi(ev));
      setDurataEffettiva(minuti ? String(minuti) : "");
    } catch (err) {
      console.error("Errore salvataggio durata effettiva:", err);
      setErrore("Errore nel salvataggio della durata effettiva.");
    }
    setSalvandoDurata(false);
  }

  function sogliaRossaSecondi() {
    if (!durataEventoSecondi) return null;
    return durataEventoSecondi / 3;
  }

  function isRosso(p) {
    const soglia = sogliaRossaSecondi();
    if (!soglia) return false;
    const durata = p.durataSecondi || p.durataMinuti * 60;
    return durata < soglia;
  }

  function esportaExcel() {
    const wb = XLSX.utils.book_new();

    // Data evento per nome foglio
    const dataEvento = evento?.dataInizio || evento?.data || "";
    const dataFormattata = dataEvento
      ? new Date(dataEvento).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }).replace(/\//g, "-")
      : "00-00-00";

    const soglia = durataEventoSecondi ? durataEventoSecondi / 3 : null;

    // Foglio 1: elenco partecipanti
    const foglio1Data = partecipantiFiltrati.map((p) => {
      const { nome, cognome } = nomeCognomePartecipante(p);
      return {
        "Cognome": cognome,
        "Nome": nome,
        "Email": p.email || "",
        "Permanenza (hh:mm:ss)": secondiInHHMMSS(p.durataSecondi || p.durataMinuti * 60),
      };
    });

    const ws1 = XLSX.utils.json_to_sheet(foglio1Data);
    ws1["!cols"] = [{ wch: 20 }, { wch: 20 }, { wch: 40 }, { wch: 20 }];

    // Grassetto intestazioni foglio 1
    ["A1", "B1", "C1", "D1"].forEach((cell) => {
      if (ws1[cell]) ws1[cell].s = { font: { bold: true } };
    });

    // Evidenzia in giallo chi ha presenziato meno di 1/3
    if (soglia) {
      partecipantiFiltrati.forEach((p, idx) => {
        const durata = p.durataSecondi || p.durataMinuti * 60;
        if (durata < soglia) {
          const row = idx + 2;
          ["A", "B", "C", "D"].forEach((col) => {
            const cell = `${col}${row}`;
            if (!ws1[cell]) ws1[cell] = { v: "", t: "s" };
            ws1[cell].s = { fill: { fgColor: { rgb: "FFFF00" } } };
          });
        }
      });
    }

    // Foglio 2: pivot per dominio
    const dominiCount = {};
    partecipanti.forEach((p) => {
      const dominio = p.email?.split("@")[1] || "sconosciuto";
      dominiCount[dominio] = (dominiCount[dominio] || 0) + 1;
    });

    const foglio2Data = Object.entries(dominiCount)
      .sort((a, b) => b[1] - a[1])
      .map(([dominio, count]) => ({ "Dominio": dominio, "Totale": count }));

    const totaleComplessivo = Object.values(dominiCount).reduce((a, b) => a + b, 0);
    foglio2Data.push({ "Dominio": "Totale complessivo", "Totale": totaleComplessivo });

    const ws2 = XLSX.utils.json_to_sheet(foglio2Data);
    ws2["!cols"] = [{ wch: 35 }, { wch: 15 }];

    // Grassetto intestazioni e riga totale foglio 2
    ["A1", "B1"].forEach((cell) => {
      if (ws2[cell]) ws2[cell].s = { font: { bold: true } };
    });
    const rigaTotale = foglio2Data.length + 1;
    [`A${rigaTotale}`, `B${rigaTotale}`].forEach((cell) => {
      if (!ws2[cell]) ws2[cell] = { v: "", t: "s" };
      ws2[cell].s = { font: { bold: true } };
    });

    XLSX.utils.book_append_sheet(wb, ws1, `Report_Attendee_${dataFormattata}`);
    XLSX.utils.book_append_sheet(wb, ws2, "Partecipanti_per_Azienda");

    XLSX.writeFile(wb, `presenze_${evento?.titolo || id}.xlsx`);
  }

  const partecipantiFiltrati = partecipanti.filter((p) => {
    const s = ricerca.toLowerCase();
    return (
      p.nome?.toLowerCase().includes(s) ||
      p.email?.toLowerCase().includes(s)
    );
  });

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  const nomeCliente = clientiMap[evento?.clienteId] || "";

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/")}
          variant="outlined" size="small">
          Lista eventi
        </Button>
        <Box>
          <Typography variant="h5" fontWeight="bold">Report presenze</Typography>
          <Typography variant="body2" color="text.secondary">
            {evento?.titolo} {nomeCliente ? `— ${nomeCliente}` : ""}
          </Typography>
        </Box>
      </Box>

      {/* CARICA FILE */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={1} color="primary">
          Carica report presenze
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Carica il file CSV esportato da Microsoft Teams.
          Formati supportati: Meeting, Webinar, Assemblea Virtuale.
          {durataEventoSecondi && (
            <> Durata {evento?.durataEffettivaMinuti > 0 ? "effettiva" : "programmata"} evento: <strong>{minutiInStringa(Math.round(durataEventoSecondi / 60))}</strong>.
            Partecipanti con meno di 1/3 della durata saranno evidenziati in rosso.</>
          )}
        </Typography>
        <Button variant="outlined" component="label" disabled={caricando}
          startIcon={caricando ? <CircularProgress size={18} /> : <UploadFileIcon />}>
          {caricando ? "Elaborazione in corso..." : "Seleziona file CSV"}
          <input type="file" accept=".csv" hidden onChange={handleCaricaFile} />
        </Button>
        {partecipanti.length > 0 && (
          <Box mt={3}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Abbina iscritti (Excel)</Typography>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              Carica l'elenco iscritti (colonne "First Name"/"Last Name" o "Nome"/"Cognome", ed eventualmente "Email")
              per prendere nome e cognome dal file invece di dividerli automaticamente.
              L'abbinamento avviene per email e, in mancanza, per nome. Va ripetuto se ricarichi il CSV Teams.
            </Typography>
            <Button variant="outlined" component="label" disabled={abbinando}
              startIcon={abbinando ? <CircularProgress size={18} /> : <GroupIcon />}>
              {abbinando ? "Abbinamento in corso..." : "Seleziona file iscritti"}
              <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleCaricaIscritti} />
            </Button>
            {esitoAbbinamento && (
              <Alert severity={esitoAbbinamento.nonTrovati ? "warning" : "success"} sx={{ mt: 2 }}>
                {esitoAbbinamento.file}: {esitoAbbinamento.trovati} partecipanti abbinati
                {esitoAbbinamento.nonTrovati > 0 &&
                  `, ${esitoAbbinamento.nonTrovati} non trovati tra gli iscritti (nome e cognome divisi automaticamente, segnalati con ?)`}.
              </Alert>
            )}
          </Box>
        )}

        {errore && <Alert severity="error" sx={{ mt: 2 }}>{errore}</Alert>}
      </Paper>

      {/* DURATA EFFETTIVA */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={1} color="primary">
          Durata effettiva incontro
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Se l'incontro è durato più o meno del previsto, indica qui la durata reale in minuti:
          verrà usata al posto di quella programmata
          {(() => {
            const programmata = evento && calcolaDurataProgrammataSecondi(evento);
            return programmata ? <> (<strong>{minutiInStringa(Math.round(programmata / 60))}</strong>)</> : null;
          })()} per calcolare la soglia di 1/3 della presenza. Lascia vuoto per usare la durata programmata.
        </Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField size="small" type="number" label="Durata effettiva"
            value={durataEffettiva} onChange={(e) => setDurataEffettiva(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
            sx={{ width: 200 }} />
          <Button variant="contained" size="small" disabled={salvandoDurata}
            onClick={() => salvaDurataEffettiva(durataEffettiva.trim())}>
            Salva
          </Button>
          {evento?.durataEffettivaMinuti > 0 && (
            <Button size="small" disabled={salvandoDurata} onClick={() => salvaDurataEffettiva("")}>
              Usa durata programmata
            </Button>
          )}
        </Box>
      </Paper>

      {/* KPI */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#E6F1FB" }}>
              <Typography variant="h3" fontWeight="bold" color="primary">{stats.totale}</Typography>
              <Typography variant="body2" color="text.secondary">Partecipanti unici</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#EAF3DE" }}>
              <Typography variant="h3" fontWeight="bold" color="success.main">
                {minutiInStringa(stats.durataMedia)}
              </Typography>
              <Typography variant="body2" color="text.secondary">Durata media presenza</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#FAEEDA" }}>
              <Typography variant="h3" fontWeight="bold" color="warning.main">
                {partecipanti.filter((p) => !isRosso(p)).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Presenti ≥ 1/3 durata evento
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* LISTA PARTECIPANTI */}
      {partecipanti.length > 0 && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">Elenco partecipanti</Typography>
            <Box display="flex" gap={1} alignItems="center">
              <Chip label={`${partecipantiFiltrati.length} risultati`} size="small" />
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
                onClick={esportaExcel}>
                Esporta Excel
              </Button>
            </Box>
          </Box>

          <TextField fullWidth placeholder="Cerca per nome o email..."
            value={ricerca} onChange={(e) => setRicerca(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ mb: 2 }} size="small" />

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Cognome</strong></TableCell>
                  <TableCell><strong>Nome</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell align="right"><strong>Permanenza</strong></TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {partecipantiFiltrati.map((p, idx) => {
                  const { nome, cognome, daVerificare } = nomeCognomePartecipante(p);
                  return (
                  <TableRow key={p.id} hover
                    sx={{ backgroundColor: isRosso(p) ? "#FDECEA" : "inherit" }}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell sx={{ color: isRosso(p) ? "error.main" : "inherit" }}>
                      {cognome || "—"}
                      {daVerificare && p.abbinatoIscritti === false && (
                        <Tooltip title="Non trovato tra gli iscritti: nome e cognome divisi automaticamente">
                          <HelpOutlineIcon fontSize="inherit" color="warning" sx={{ ml: 0.5, verticalAlign: "middle" }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: isRosso(p) ? "error.main" : "inherit" }}>
                      {nome || "—"}
                    </TableCell>
                    <TableCell sx={{ color: isRosso(p) ? "error.main" : "inherit" }}>
                      {p.email || "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={secondiInHHMMSS(p.durataSecondi || p.durataMinuti * 60)}
                        size="small"
                        color={isRosso(p) ? "error" : "success"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right" padding="none">
                      <Tooltip title="Elimina dal report">
                        <IconButton size="small" aria-label={`Elimina ${p.nome} dal report`}
                          onClick={() => eliminaPartecipante(p)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}